/* eslint-disable no-use-before-define */
import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import CockpitSyncStatsRefNavigation from './CockpitSyncStatsRefNavigation';
import { chargerCockpitSyncStatsRef } from './cockpitSyncStatsRefApi';

const INTERVALLE_REFRESH_MS = 60000;

const useStyles = makeStyles((theme) => ({
  page: { paddingTop: theme.spacing(3), paddingBottom: theme.spacing(4), textAlign: 'left' },
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  sectionTitre: { marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
  muted: { color: '#666', fontSize: 12 },
  tableScroll: { overflowX: 'auto' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: theme.spacing(1), marginTop: theme.spacing(1) },
  kpiBox: { border: '1px solid #e5e7eb', borderRadius: 6, padding: theme.spacing(1), backgroundColor: '#fff' },
  kpiValeur: { fontWeight: 700, fontSize: 20, lineHeight: 1.2 },
  kpiLabel: { color: '#6b7280', fontSize: 11, textTransform: 'uppercase' },
  chipVert: { backgroundColor: '#ecfdf5', color: '#065f46', fontSize: 11 },
  chipOrange: { backgroundColor: '#fff7ed', color: '#9a3412', fontSize: 11 },
  chipRouge: { backgroundColor: '#fef2f2', color: '#991b1b', fontSize: 11 },
  chipGris: { backgroundColor: '#f3f4f6', color: '#374151', fontSize: 11 },
  erreurBox: { borderLeft: '4px solid #f59e0b', padding: theme.spacing(1.5), backgroundColor: '#fff7ed', marginBottom: theme.spacing(2) },
  videBox: { padding: theme.spacing(4), textAlign: 'center', color: '#6b7280' },
  detailsBrut: { marginTop: theme.spacing(0.5), borderTop: '1px solid #e5e7eb', paddingTop: theme.spacing(0.5) },
  summaryBrut: { cursor: 'pointer', color: '#374151', fontSize: 12, fontWeight: 600 },
  contenuBrut: { fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-word', maxWidth: 600, color: '#374151' },
}));

const CockpitSyncStatsRef = () => {
  const classes = useStyles();
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const timerRef = useRef(null);

  const charger = () => {
    setChargement(true);
    chargerCockpitSyncStatsRef()
      .then((payload) => { setDonnees(payload); setErreur(''); })
      .catch((err) => { setErreur(err.message); })
      .finally(() => setChargement(false));
  };

  useEffect(() => {
    charger();
    timerRef.current = setInterval(charger, INTERVALLE_REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const appareils = (donnees && donnees.appareils) || [];
  const activite = (donnees && donnees.activiteRecente) || [];
  const sante = (donnees && donnees.santeOperationnelle) || {};
  const alertes = (donnees && donnees.alertes) || [];
  const avertissements = (donnees && donnees.avertissements) || [];

  return (
    <Container maxWidth={false} className={classes.page}>
      <CockpitSyncStatsRefNavigation />

      {erreur && (
        <div className={classes.erreurBox}>
          <Typography variant="body2">Erreur : {erreur}</Typography>
        </div>
      )}

      {avertissements.length > 0 && (
        <div className={classes.erreurBox}>
          <Typography variant="body2">
            Avertissements : {avertissements.join(', ')}
          </Typography>
        </div>
      )}

      {!chargement && appareils.length === 0 && !erreur && (
        <Paper className={classes.videBox}>
          <Typography variant="h6" gutterBottom>Aucun appareil SyncStatsRef detecte</Typography>
          <Typography variant="body2" className={classes.muted}>
            Les donnees apparaitront ici lorsque le poller commencera a ingerer des fichiers SyncStatsRef.
          </Typography>
        </Paper>
      )}

      {appareils.length > 0 && (
        <React.Fragment>
          <SectionStatutAppareils appareils={appareils} classes={classes} />
          <SectionActiviteRecente activite={activite} classes={classes} />
          <SectionSanteOperationnelle sante={sante} classes={classes} />
          <SectionAlertes alertes={alertes} classes={classes} />
        </React.Fragment>
      )}

      {chargement && !donnees && (
        <Typography variant="body2" className={classes.muted}>Chargement...</Typography>
      )}
    </Container>
  );
};

// --- Section 1 : Statut appareils ---

function SectionStatutAppareils({ appareils, classes }) {
  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" className={classes.sectionTitre}>Statut appareils</Typography>
      <div className={classes.tableScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Appareil</TableCell>
              <TableCell>Dernier signal</TableCell>
              <TableCell>Batterie</TableCell>
              <TableCell>Reseau</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Dernier sync</TableCell>
              <TableCell>Sante</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {appareils.map((a) => (
              <TableRow key={a.tel_id}>
                <TableCell>{a.tel_id}</TableCell>
                <TableCell style={{ fontFamily: 'monospace', fontSize: 11 }}>
                  {formaterDate(a.dernier_signal_at)}
                </TableCell>
                <TableCell>
                  {a.batterie !== null ? `${a.batterie}%` : '—'}
                </TableCell>
                <TableCell>{a.reseau || '—'}</TableCell>
                <TableCell>{a.version || '—'}</TableCell>
                <TableCell style={{ fontFamily: 'monospace', fontSize: 11 }}>
                  {formaterDate(a.dernier_sync_at)}
                </TableCell>
                <TableCell>
                  <ChipSante sante={a.sante} classes={classes} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Paper>
  );
}

// --- Section 2 : Activité récente ---

function SectionActiviteRecente({ activite, classes }) {
  if (activite.length === 0) return null;

  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" className={classes.sectionTitre}>Activite recente</Typography>
      <div className={classes.tableScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Heure</TableCell>
              <TableCell>Appareil</TableCell>
              <TableCell>Evenement</TableCell>
              <TableCell>Niveau</TableCell>
              <TableCell>Resume</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activite.slice(0, 50).map((ev) => (
              <TableRow key={ev.ligne_id}>
                <TableCell style={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' }}>
                  {formaterHeure(ev.ts_event)}
                </TableCell>
                <TableCell>{ev.tel_id}</TableCell>
                <TableCell>{ev.evenement_type || '—'}</TableCell>
                <TableCell>
                  <ChipNiveau niveau={ev.niveau} classes={classes} />
                </TableCell>
                <TableCell>
                  <span style={{ fontSize: 12 }}>{tronquer(ev.resume_payload, 80)}</span>
                  {ev.contenu_brut && (
                    <details className={classes.detailsBrut}>
                      <summary className={classes.summaryBrut}>brut</summary>
                      <div className={classes.contenuBrut}>{ev.contenu_brut}</div>
                    </details>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {activite.length > 50 && (
        <Typography variant="body2" className={classes.muted} style={{ marginTop: 8 }}>
          {activite.length - 50} evenements supplementaires non affiches.
        </Typography>
      )}
    </Paper>
  );
}

// --- Section 3 : Santé opérationnelle ---

function SectionSanteOperationnelle({ sante, classes }) {
  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" className={classes.sectionTitre}>Sante operationnelle</Typography>
      <div className={classes.kpiGrid}>
        <Kpi
          classes={classes}
          valeur={sante.nbAppareilsActifs != null ? sante.nbAppareilsActifs : '—'}
          label="Appareils actifs"
          couleur={sante.nbAppareilsActifs > 0 ? '#065f46' : '#6b7280'}
        />
        <Kpi
          classes={classes}
          valeur={sante.nbAppareilsSilencieux != null ? sante.nbAppareilsSilencieux : '—'}
          label="Appareils silencieux"
          couleur={sante.nbAppareilsSilencieux > 0 ? '#991b1b' : '#065f46'}
        />
        <Kpi
          classes={classes}
          valeur={sante.syncMoyenMs !== null && sante.syncMoyenMs !== undefined ? formaterDureeMs(sante.syncMoyenMs) : '—'}
          label="Sync moyen"
          couleur="#374151"
        />
        <Kpi
          classes={classes}
          valeur={sante.driftHorlogeMaxMs !== null && sante.driftHorlogeMaxMs !== undefined ? `${sante.driftHorlogeMaxMs} ms` : '—'}
          label="Drift horloge max"
          couleur={sante.driftHorlogeMaxMs > 500 ? '#9a3412' : '#374151'}
        />
      </div>
    </Paper>
  );
}

// --- Section 4 : Alertes simples ---

function SectionAlertes({ alertes, classes }) {
  if (alertes.length === 0) {
    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitre}>Alertes</Typography>
        <Typography variant="body2" className={classes.muted}>Aucune alerte active.</Typography>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" className={classes.sectionTitre}>Alertes</Typography>
      <div className={classes.tableScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Appareil</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Niveau</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alertes.map((alerte, idx) => (
              <TableRow key={`${alerte.type}-${alerte.tel_id}-${idx}`}>
                <TableCell>{libelleTypeAlerte(alerte.type)}</TableCell>
                <TableCell>{alerte.tel_id || '—'}</TableCell>
                <TableCell style={{ fontSize: 12 }}>{alerte.message}</TableCell>
                <TableCell>
                  <ChipSante sante={alerte.niveau} classes={classes} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Paper>
  );
}

// --- Composants utilitaires ---

function Kpi({ classes, valeur, label, couleur }) {
  return (
    <div className={classes.kpiBox}>
      <div className={classes.kpiValeur} style={{ color: couleur }}>{valeur}</div>
      <div className={classes.kpiLabel}>{label}</div>
    </div>
  );
}

function ChipSante({ sante, classes }) {
  const classeMap = { vert: classes.chipVert, orange: classes.chipOrange, rouge: classes.chipRouge };
  const labelMap = { vert: 'OK', orange: 'Attention', rouge: 'Critique' };
  return (
    <Chip
      size="small"
      label={labelMap[sante] || sante}
      className={classeMap[sante] || classes.chipGris}
    />
  );
}

function ChipNiveau({ niveau, classes }) {
  const n = (niveau || '').toUpperCase();
  if (n.includes('ERROR') || n.includes('CRITIQUE')) return <Chip size="small" label={niveau} className={classes.chipRouge} />;
  if (n.includes('WARN') || n.includes('DEGRADE')) return <Chip size="small" label={niveau} className={classes.chipOrange} />;
  return <Chip size="small" label={niveau} className={classes.chipGris} />;
}

// --- Fonctions de formatage ---

function formaterDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-CA', { timeZone: 'America/Toronto', hour12: false });
  } catch (e) { return iso; }
}

function formaterHeure(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('fr-CA', { timeZone: 'America/Toronto', hour12: false });
  } catch (e) { return iso; }
}

function formaterDureeMs(ms) {
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function tronquer(texte, max) {
  if (!texte) return '';
  return texte.length > max ? texte.slice(0, max) + '...' : texte;
}

function libelleTypeAlerte(type) {
  const labels = {
    silence_appareil: 'Silence',
    batterie_faible: 'Batterie',
    drift_horloge: 'Drift horloge',
    sync_echecs_repetes: 'Sync echecs',
  };
  return labels[type] || type;
}

export default CockpitSyncStatsRef;



// WEBPACK FOOTER //
// src/components/cockpit/CockpitSyncStatsRef.js